/**
 * 搜索记录（Alice 页面 search-records 的复刻）：联网搜索与网页读取的逐次调用。
 *
 * 背景：Alice 有自己的联网搜索服务，这页展示每次调用的引擎、回退与成本。本仓网关没有联网搜索与网页读取接口，
 *   没有可展示的数据。产品要求不造演示数据，所以保留 Alice 的整页结构（指标卡、筛选栏、十列表格），
 *   数据区显示空状态并说明原因；接口 /control/admin/stats/search-records 恒返回 supported=false。
 * 设计意图：页面与接口都先注册好，以后接入联网搜索时只需要让接口返回记录，前端表格列已经对齐。
 * 关键约束：不渲染任何虚构行；筛选控件保留，点「查询」仍会请求接口，保证接口接入后无需再改页面。
 */
import { useCallback, useEffect, useState } from 'react'
import type { SearchRecords as SearchData } from '@ap/api-client/admin-runtime'
import { runtimeApi } from '@ap/api-client/admin-runtime'
import { AG_runtime as G, errorTextRuntime } from '@ap/i18n/admin-runtime'
import { useI18n } from '@ap/i18n'
import { useToast } from '@ap/ui'
import { OpsNote, OpsPage, Stat, Stats, TableWrap, Toolbar } from '../../alice/kit.tsx'
import { addDays, fmtInt, todayLocal, useMoney } from '../../alice/kit-runtime.tsx'
import { EmptyRow, ToolInput, ToolSelect, fieldStyle } from '../../alice/kit-runtime2.tsx'

export function SearchRecords() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const money = useMoney()
  const [data, setData] = useState<SearchData | null>(null)
  const [start, setStart] = useState(addDays(todayLocal(), -29))
  const [end, setEnd] = useState(todayLocal())
  const [user, setUser] = useState('')
  const [keyword, setKeyword] = useState('')
  const [engine, setEngine] = useState('')
  const [status, setStatus] = useState('')
  const [internal, setInternal] = useState(true)

  const load = useCallback(async () => {
    try {
      setData(await runtimeApi.searchRecords({ limit: 50 }))
    } catch (e) {
      console.error('[search-records] 加载失败', e)
      toast.error(errorTextRuntime(e, locale))
    }
  }, [toast, locale])

  useEffect(() => {
    void load()
  }, [load])

  const quick = (days: number) => {
    const today = todayLocal()
    setStart(addDays(today, -(days - 1)))
    setEnd(today)
  }
  const reset = () => {
    setUser('')
    setKeyword('')
    setEngine('')
    setStatus('')
    setInternal(true)
    quick(30)
    void load()
  }
  const dash = '—'

  return (
    <OpsPage eyebrow={t(G.search.eyebrow)} title={t(G.search.title)} desc={t(G.search.desc)} chip={t(G.search.chip)}>
      <Stats>
        <Stat label={t(G.search.stTotal)} value={fmtInt(data?.total ?? 0)} />
        <Stat label={t(G.search.stFailed)} value={fmtInt(data?.total_failed ?? 0)} sub={t(G.search.stFailedSub)} />
        <Stat label={t(G.search.stFallback)} value={fmtInt(data?.total_fallback ?? 0)} sub={t(G.search.stFallbackSub)} />
        <Stat label={t(G.search.stCost)} value={data ? money.usd(data.upstream_cost, 4) : dash} sub={t(G.search.stCostSub, { v: money.petals(data?.revenue ?? 0) })} />
      </Stats>

      <Toolbar>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={fieldStyle} />
        <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{t(G.common.to)}</span>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={fieldStyle} />
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => quick(1)}>
          {t(G.search.today)}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => quick(7)}>
          {t(G.search.last7)}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => quick(30)}>
          {t(G.search.last30)}
        </button>
        <ToolInput width={180} placeholder={t(G.search.phUser)} value={user} onChange={(e) => setUser(e.target.value)} onEnter={() => void load()} />
        <ToolInput width={200} placeholder={t(G.search.phKeyword)} value={keyword} onChange={(e) => setKeyword(e.target.value)} onEnter={() => void load()} />
        <ToolSelect value={engine} onChange={(e) => setEngine(e.target.value)} options={[{ value: '', label: t(G.search.allEngines) }]} />
        <ToolSelect
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={[
            { value: '', label: t(G.search.allStatus) },
            { value: 'ok', label: t(G.search.stOk) },
            { value: 'failed', label: t(G.search.stFail) },
            { value: 'fallback', label: t(G.search.stFb) },
            { value: 'no_results', label: t(G.search.stNoResults) },
          ]}
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
          {t(G.search.exclInternal)}
        </label>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => void load()}>
          {t(G.search.query)}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={reset}>
          {t(G.search.reset)}
        </button>
      </Toolbar>

      <OpsNote>{t(G.search.note)}</OpsNote>

      <TableWrap
        head={
          <>
            <th>{t(G.search.cTime)}</th>
            <th>{t(G.search.cUser)}</th>
            <th>{t(G.search.cCapability)}</th>
            <th>{t(G.search.cContent)}</th>
            <th>{t(G.search.cStatus)}</th>
            <th>{t(G.search.cEngine)}</th>
            <th>{t(G.search.cDuration)}</th>
            <th>{t(G.search.cCharge)}</th>
            <th>{t(G.search.cUpstream)}</th>
            <th>{t(G.search.cAction)}</th>
          </>
        }
      >
        <EmptyRow cols={10}>{data && !data.supported ? t(G.search.unsupported) : t(G.search.empty)}</EmptyRow>
      </TableWrap>
    </OpsPage>
  )
}
